import React from 'react';
import Button from '../Button';
import Col from '../Col';
import { GenericDialogue } from '../Dialogue/Dialogue';
import Row from '../Row';
import 'react-toastify/dist/ReactToastify.css';
import LoadingButton from '../BulkOperations/_Shared/BulkActionButton/LoadingButton';
import { variables } from 'src/Tenants';
import Callout from 'src/components/InformationCallout/Callout';
import Toggle from 'src/components/Toggle/Toggle';
import NewBadge from 'src/components/NewBadge/NewBadge';
import TextEditor from '../Editor/Editor';
import OtherDetails from './Components/OtherDetails/OtherDetails';
import SuiteDetailsSection from './sections/SuiteDetailsSection';
import PropertyDetailsSection from './sections/PropertyDetailsSection';
import ParkingStorageSection from './sections/ParkingStorageSection';
import KeyDatesSection from './sections/KeyDatesSection';
import WarrantyDetailsSection from './sections/WarrantyDetailsSection';
import SuiteAddedIllustration from '../SuiteAddedIllustration/SuiteAddedIllustration';
import {
  useSuiteFormOrchestrator,
  UseSuiteFormOrchestratorReturn,
} from './hooks/useSuiteFormOrchestrator';
import './SuiteForm.scss';

const SuiteForm: React.FC<any> = ({
  initialSuite,
  onClose,
  onSaveSuite,
  confirmOnSave,
}) => {
  const {
    suiteFormData,
    statusOptions,
    selectedStatusOption,
    state,
    suiteKeysAndValues,
    isSparkDisabled,

    floorplanFiles,
    floorplanImage,
    showPdfFloorplanLink,
    isFloorplanStaged,
    stagedFloorplanName,
    buildingOptions,
    levelOptions,
    selectedBuildingOption,
    selectedLevelOption,

    onSetSuiteFormData,
    onToggleCommonArea,
    onEditorChange,
    onSave,
    onAddAnother,
    onValidateFile,
    onClearFloorplan,
    handleSelectedStatus,
    onChangeBuilding,
    onChangeLevel,
    onChangeOrientationDate,
    onChangeCompletionDate,
    onChangePossessionDate,
    onCheckExistingParkingStallStorageLockers,
    onAddPair,
    onRemovePair,
    onInputChange,
  }: UseSuiteFormOrchestratorReturn = useSuiteFormOrchestrator({
    initialSuite,
    onClose,
    onSaveSuite,
    confirmOnSave,
  });

  return (
    <GenericDialogue
      isOpen
      onClose={onClose}
      minHeight="97vh"
      maxHeight='97vh'
      maxWidth='125rem'
      title={
        state.showConfirmation
          ? `${variables.Suite} added`
          : (suiteFormData && suiteFormData.id ? 'Edit' : 'Add') +
            ' ' +
            variables.Suite
      }
      footer={
        state.showConfirmation ? (
          <>
            <Button
              color="semantic"
              size="small"
              label={`Add Another ${variables.Suite}`}
              onClick={onAddAnother}
            />
            <Button
              color="teal"
              size="small"
              label="Done"
              style={{ marginLeft: '1rem' }}
              onClick={onClose}
            />
          </>
        ) : (
          <>
            {!confirmOnSave && suiteFormData && !suiteFormData.id ? (
              <Button
                color="semantic"
                size="small"
                label="Save and Add Another"
                disabled={
                  (suiteFormData && suiteFormData.number
                    ? !suiteFormData.number.trim()
                    : !suiteFormData.number) ||
                  !state.areas.length ||
                  suiteFormData.statusId ===
                    '00000000-0000-0000-0000-000000000000'
                }
                onClick={() => onSave('SaveAndAdd')}
              />
            ) : null}

            <LoadingButton
              actionText={'Save ' + variables.Suite}
              isSubmitting={state.loading}
              disabled={
                (suiteFormData && suiteFormData.number
                  ? !suiteFormData.number.trim()
                  : !suiteFormData.number) ||
                suiteFormData.statusId ===
                  '00000000-0000-0000-0000-000000000000'
              }
              onClick={() => onSave('SaveAndClose')}
            />
          </>
        )
      }
    >
      {state.showConfirmation ? (
        <div className="suite-form suite-form-confirmation">
          <SuiteAddedIllustration className="suite-form-confirmation-illustration" />
          <h2 className="suite-form-confirmation-heading">
            {variables.Suite} added
          </h2>
          <p className="suite-form-confirmation-body">
            Your {variables.Suite.toLowerCase()} has been saved. Add another to
            this property, or you're all done.
          </p>
        </div>
      ) : (
        <div className="suite-form">
          <SuiteDetailsSection
            suiteFormData={suiteFormData}
            statusOptions={statusOptions}
            selectedStatus={selectedStatusOption}
            isSparkDisabled={isSparkDisabled}
            floorImage={state.floorImage}
            floorplanFiles={floorplanFiles}
            floorplanImage={floorplanImage}
            floorplanError={state.errors.floorPlanURL}
            showPdfFloorplanLink={showPdfFloorplanLink}
            isFloorplanStaged={isFloorplanStaged}
            stagedFloorplanName={stagedFloorplanName}
            onSetSuiteFormData={onSetSuiteFormData}
            onSelectStatus={handleSelectedStatus}
            onValidateFile={onValidateFile}
            onClearFloorplan={onClearFloorplan}
          />
          <Row style={{ marginTop: '5px' }}>
            <Col xs="12" md="12">
              <div className="suite-form-common-area">
                <Callout
                  variant="info"
                  title={
                    <>
                      Common area
                      <NewBadge spaced />
                    </>
                  }
                  subtitle={`Common areas are shared spaces (lobbies, gyms, parking) rather than individual units. Marking one here groups it separately and is where we recommend assigning ${variables.strataManagers}.`}
                />
                <Toggle
                  label={`This ${variables.Suite.toLowerCase()} is a common area`}
                  value={!!suiteFormData.isCommonArea}
                  onToggle={() => onToggleCommonArea()}
                />
              </div>
            </Col>
          </Row>
          <PropertyDetailsSection
            suiteFormData={suiteFormData}
            isSparkDisabled={isSparkDisabled}
            buildingOptions={buildingOptions}
            levelOptions={levelOptions}
            selectedBuilding={selectedBuildingOption}
            selectedLevel={selectedLevelOption}
            onSetSuiteFormData={onSetSuiteFormData}
            onChangeBuilding={onChangeBuilding}
            onChangeLevel={onChangeLevel}
          />
          <ParkingStorageSection
            suiteFormData={suiteFormData}
            isSparkDisabled={isSparkDisabled}
            checkingSlotType={state.checkingSlotType}
            onCheckExistingParkingStallStorageLockers={
              onCheckExistingParkingStallStorageLockers
            }
            onSetSuiteFormData={onSetSuiteFormData}
          />
          <OtherDetails
            keyValuePairs={suiteKeysAndValues}
            onInputChange={onInputChange}
            onAddPair={onAddPair}
            onRemovePair={onRemovePair}
          />
          <Row style={{ marginTop: '5px' }}>
            <Col xs="12" md="12">
              <div className="input">
                <div className="reporting__heading suite-form-label">
                  Key Pickup Instructions :
                </div>
                <div
                  style={{
                    borderBottom: '0.1rem solid rgba(83, 83, 83, 0.25)',
                  }}
                  className="input__control"
                >
                  <TextEditor
                    editorState={state.editorState}
                    onEditorStateChange={onEditorChange}
                  />
                </div>
              </div>
            </Col>
          </Row>
          <KeyDatesSection
            orientationDate={state.orientationDate}
            completionDate={state.completionDate}
            posessionDate={state.posessionDate}
            isSparkDisabled={isSparkDisabled}
            onChangeOrientationDate={onChangeOrientationDate}
            onChangeCompletionDate={onChangeCompletionDate}
            onChangePossessionDate={onChangePossessionDate}
          />
          <WarrantyDetailsSection
            suiteFormData={suiteFormData}
            onSetSuiteFormData={onSetSuiteFormData}
          />
        </div>
      )}
    </GenericDialogue>
  );
};

export default SuiteForm;
